import {
  PUBLIC_ERR_BURST_CAP,
  PUBLIC_ERR_COOLDOWN_MINUTES,
  PUBLIC_ERR_GLOBAL_MAX,
  PUBLIC_ERR_GLOBAL_WINDOW_SEC,
  PUBLIC_ERR_THROTTLE_ENABLED,
  SENTRY_NODE_ENV,
} from '@lib/constants'

export interface ThrottleConfig {
  /** When false the throttle is a no-op and every error is forwarded. */
  enabled: boolean
  /** Occurrences of one fingerprint that send before it is muted. */
  burstCap: number
  /** How long a fingerprint stays muted after its burst is spent (ms). */
  cooldownMs: number
  /** Rolling window for the cross-fingerprint backstop (ms). */
  globalWindowMs: number
  /** Max sends across all fingerprints within `globalWindowMs`. */
  globalMax: number
}

const MINUTE = 60_000

// Env-tiered defaults. Production mutes aggressively to save Datadog cost; QA
// mutes briefly so testers still see errors during a session; development is off
// so everything surfaces locally.
const DEFAULTS: Record<'production' | 'qa' | 'development', ThrottleConfig> = {
  production: { enabled: true, burstCap: 3, cooldownMs: 4 * 60 * MINUTE, globalWindowMs: MINUTE, globalMax: 50 },
  qa: { enabled: true, burstCap: 3, cooldownMs: 30 * MINUTE, globalWindowMs: MINUTE, globalMax: 100 },
  development: { enabled: false, burstCap: 3, cooldownMs: 0, globalWindowMs: MINUTE, globalMax: Infinity },
}

const pickDefaults = (): ThrottleConfig => {
  if (SENTRY_NODE_ENV === 'production') return DEFAULTS.production
  if (SENTRY_NODE_ENV === 'qa') return DEFAULTS.qa

  return DEFAULTS.development
}

// Parse a numeric env override, ignoring blank/non-numeric values so a typo
// falls back to the default rather than disabling throttling.
const num = (raw: string | number | undefined, fallback: number): number => {
  if (typeof raw === 'number') return raw
  if (raw === undefined || raw.trim() === '') return fallback
  const parsed = Number(raw)

  return Number.isFinite(parsed) ? parsed : fallback
}

export const resolveThrottleConfig = (): ThrottleConfig => {
  const base = pickDefaults()

  return {
    enabled: PUBLIC_ERR_THROTTLE_ENABLED === undefined ? base.enabled : PUBLIC_ERR_THROTTLE_ENABLED !== 'false',
    burstCap: num(PUBLIC_ERR_BURST_CAP, base.burstCap),
    cooldownMs: num(PUBLIC_ERR_COOLDOWN_MINUTES, base.cooldownMs / MINUTE) * MINUTE,
    globalWindowMs: num(PUBLIC_ERR_GLOBAL_WINDOW_SEC, base.globalWindowMs / 1000) * 1000,
    globalMax: num(PUBLIC_ERR_GLOBAL_MAX, base.globalMax),
  }
}
